import { WhatsApp } from '@mui/icons-material';
import { Skeleton, Stack, type Theme, useMediaQuery } from '@mui/material';
import { useMemo, useState } from 'react';
import MobileHeader from '/@/components/bayer/header/mobile-header';
import PageContainer from '/@/components/bayer/page-container';
import Icon from '/@/components/icon';
import useUnitProvider from '/@/core/context/unit';
import useBreadcrumbs from '/@/hooks/breadcrumbs';
import { useCropMonitoringById } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import { useFieldById } from '/@/hooks/fields';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { useGetUserNode, useOwnUserNode } from '/@/hooks/nodes';
import { useHasWriteAccess, useIsValidatedRV } from '/@/hooks/session';
import { _ } from '/@/locale';
import { openWhatsappWeb } from '/@/utils';
import AncestorsBarV2 from '/@/views/dashboard/components/ancestors-bar-v2';
import FieldviewChip from '/@/views/dashboard/components/fieldview-chip';
import CreateRecommendationModal from '/@/views/dashboard/rv/components/create-recommendation-modal';
import Header from '../../../../components/bayer/header';
import CropMonitoringDialog from '../components/creation-dialog';
import CropMonitoringMoreOptions from '../components/more-options';
import FieldMonitoringDashboardCurrentCampaign from './tabs/current-campaign';

const FieldDashboard = ({ cropMonitoringId, fieldId }: { cropMonitoringId?: number; fieldId?: number }) => {
    const units = useUnitProvider();
    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const { data: cropMonitoring, isLoading: isCMLoading } = useCropMonitoringById(cropMonitoringId ?? 0);
    const fieldQuery = useFieldById(
        { id: fieldId || cropMonitoring?.field || 0 },
        { enabled: Boolean(fieldId || cropMonitoring?.field) }
    );
    const [newRecommendationModalOpened, setNewRecommendationModalOpened] = useState(false);
    const [newCMModalOpened, setNewCMModalOpened] = useState(false);
    const ownUserNodeQuery = useOwnUserNode();

    const growerQuery = useGetUserNode(fieldQuery.data?.userNode);

    const isRv = useIsValidatedRV();
    const hasWriteAccess = useHasWriteAccess();

    const breadcrumbs = useBreadcrumbs('fieldMonitoring.FieldMonitoringDetail');

    const locationArea = useMemo(() => {
        const area = fieldQuery.data?.area ? convertAreaFromM2(fieldQuery.data.area, units).toString() : null;
        const location = fieldQuery.data?.location;
        return [area, location].filter(Boolean).join(' - ');
    }, [fieldQuery.data?.area, fieldQuery.data?.location, units]);

    return (
        <PageContainer
            grayBackground
            header={
                isSmOrLower ? (
                    <MobileHeader
                        title={fieldQuery.isValidating ? <Skeleton width={100} /> : fieldQuery.data?.name}
                        breadcrumbs={breadcrumbs}
                        titleIcon={
                            fieldQuery.data?.externalProvider === 'fieldview' && (
                                <Icon
                                    type='fieldview'
                                    style={{ fill: '#F7B618', width: '24px', height: '24px', flex: 'none' }}
                                />
                            )
                        }
                        moreButton={
                            !hasWriteAccess ? undefined : (
                                <CropMonitoringMoreOptions
                                    fieldId={fieldQuery.data?.id}
                                    cropMonitoring={cropMonitoring}
                                    disableOptions={['show']}
                                />
                            )
                        }
                    />
                ) : (
                    <Header
                        title={fieldQuery.isValidating ? <Skeleton width={100} /> : fieldQuery.data?.name}
                        titleIcon={fieldQuery.data?.externalProvider === 'fieldview' && <FieldviewChip />}
                        buttons={[
                            ...(((!cropMonitoring?.cornCropMonitoring?.phenology &&
                                !cropMonitoring?.plantingDate &&
                                !isCMLoading &&
                                !!cropMonitoring) ||
                                cropMonitoring?.cornCropMonitoring?.phenology === 'harvested') &&
                            hasWriteAccess
                                ? [
                                      {
                                          children: _('Agregar cultivo'),
                                          variant: 'contained',
                                          color: 'secondary',
                                          size: 'medium',
                                          startIcon: <Icon type='agronomy' size='xxs' sx={{ fill: 'white' }} />,
                                          onClick: () => {
                                              setNewCMModalOpened(true);
                                          },
                                      },
                                  ]
                                : []),
                            ...(!hasWriteAccess
                                ? []
                                : [
                                      {
                                          children: _('Generar una recomendación'),
                                          variant: 'outlined',
                                          color: 'secondary',
                                          size: 'medium',
                                          sx: { background: 'white' },
                                          onClick: () => {
                                              setNewRecommendationModalOpened(true);
                                          },
                                      },
                                  ]),
                            ...(isRv
                                ? [
                                      {
                                          variant: 'text',
                                          color: 'primary',
                                          size: 'small',
                                          startIcon: <WhatsApp />,
                                          sx: { color: 'primary.onSurface' },
                                          onClick: () => {
                                              openWhatsappWeb({ phone: growerQuery.data?.phone });
                                          },
                                      },
                                  ]
                                : []),
                        ]}
                        actions={
                            !hasWriteAccess
                                ? []
                                : [
                                      <CropMonitoringMoreOptions
                                          key='more-options'
                                          fieldId={fieldQuery.data?.id}
                                          cropMonitoring={cropMonitoring}
                                          disableOptions={['show']}
                                      />,
                                  ]
                        }
                        breadcrumbs={breadcrumbs}
                    />
                )
            }
        >
            {fieldQuery.data?.userNode && (
                <AncestorsBarV2
                    ancestorsFromNodeId={
                        fieldQuery.data?.userNode !== ownUserNodeQuery.data?.id ? fieldQuery.data.userNode : undefined
                    }
                    contactItems={locationArea ? [{ key: 'location-area', value: locationArea }] : undefined}
                />
            )}
            <Stack spacing={3} mt={4}>
                <FieldMonitoringDashboardCurrentCampaign
                    cropMonitoringId={cropMonitoringId}
                    fieldId={fieldId}
                    cropMonitoring={cropMonitoring}
                    isCMLoading={isCMLoading}
                />
            </Stack>
            <CreateRecommendationModal
                open={newRecommendationModalOpened}
                setOpen={setNewRecommendationModalOpened}
                selectedField={fieldQuery.data || undefined}
                userNodeId={fieldQuery.data?.userNode}
                entrySource='field-monitoring'
            />
            <CropMonitoringDialog
                open={newCMModalOpened}
                setClose={() => setNewCMModalOpened(false)}
                entry_source='field-monitoring'
                field={fieldQuery.data || undefined}
                forceNew
            />
        </PageContainer>
    );
};

export default FieldDashboard;
